import { graphql, Link } from 'gatsby';
import React, { useState } from 'react';
import { Column, Container, Row } from '../../components/Grid';
import Image from '../../components/Image';
import Layout from '../../components/Layout';
import Resource from '../../components/Resource';
import Section from '../../components/Section';
import Video from '../../components/Video';
import { getKeyedResources } from '../../utils/resource-helper';

const ClassroomResourcesPage = ({ data }) => {
  const {
    phonicsAlphabeticPrinciple,
    phonicsDecoding,
    phonicsWordStudy,
    phonicsMasterclass,
    taskCards,
    phonicsRtuAlphabetic,
    phonicsRtuDecoding,
    phonicsRtuWordStudy,
    phonicsDecodableReaders,
    phonicsRtuDecodableReadersSet1,
    phonicsRtuDecodableReadersSet2,
    phonicsRtuDecodableReadersSet3,
  } = getKeyedResources(data);
  const [isTabExpanded, setIsTabExpanded] = useState(false);

  return (
    <Layout
      title="Phonics"
      className="classroom-resources-subpage classroom-resources-subpage--phonics"
    >
      <Section className="classroom-resources-subpage-header-section">
        <Container>
          <div className="classroom-resources-subpage-header classroom-resources-subpage-header--phonics">
            <div className="classroom-resources-subpage-header-top-section">
              <Link
                to="/classroom-resources"
                className="classroom-resources-subpage-header-back-button"
              >
                <Image
                  className="classroom-resources-subpage-header-back-icon"
                  filename="back-button-icon.png"
                  alt="Back"
                />
                <span>Back to Learning Resources Overview</span>
              </Link>
              <div className="classroom-resources-subpage-header-resources-tab">
                <button
                  className={`classroom-resources-subpage-header-tab-button ${
                    isTabExpanded ? 'expanded' : ''
                  }`}
                  onClick={() => setIsTabExpanded(!isTabExpanded)}
                >
                  How to use these resources
                  <span className="caret-icon">▼</span>
                </button>
                {isTabExpanded && (
                  <div className="classroom-resources-subpage-header-tab-content">
                    <p>
                      <b>1. Start with the Explainer Video:</b> Watch the
                      Explainer video to understand the key concepts of each big
                      idea of literacy.
                      <br />
                      <b>2. Dive into the Microlearns:</b> Complete the 6–8
                      minute, self-paced modules for each big idea. These
                      modules provide research-based strategies and activities
                      you can immediately use with your students.
                      <br />
                      <b>3. See the Strategies in Action:</b> Watch the
                      masterclass video, where a teacher demonstrates these
                      strategies in a real-world classroom setting.
                      <br />
                      <b>4. Equip Yourself with Task Cards:</b> Print the task
                      cards to keep these strategies at your fingertips and use
                      the printable manipulatives to support hands-on learning
                      with students.
                      <br />
                      <b>5. Apply What You’ve Learned:</b> Use the ready-to-use
                      activity to teach foundational literacy skills like
                      phonics, vocabulary, and comprehension directly in your
                      classroom or after-school program.
                      <br />
                      <b>Why This Approach Works:</b> Each step builds on the
                      previous one, helping you gain foundational knowledge, see
                      effective strategies in practice, and implement them
                      confidently. Whether you’re a classroom teacher or
                      afterschool professional, these resources provide the
                      tools you need to create engaging, impactful literacy
                      experiences for K–2 learners.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <h1 className="classroom-resources-subpage-header-title">
              Phonics
            </h1>
            <p className="classroom-resources-subpage-header-description">
              Strengthen students’ understanding of the relationship between the
              sound of a word or letter and its literal meaning with these
              phonemics-focused resources.
            </p>
          </div>
        </Container>
      </Section>
      <Section style={{ padding: '0' }}>
        <Container>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button classroom-resources-subpage-header-back-button--mobile"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
      <Section>
        <Container>
          <div className="classroom-resources-subpage-video">
            <Video guidSrc="f729d6db-eda0-4954-bcd9-e55d0cecb585" />
            <p className="classroom-resources-subpage-video-category">
              Explainer video
            </p>
            <p className="classroom-resources-subpage-video-title">
              What is Phonics?
            </p>
            <p className="classroom-resources-subpage-video-description">
              Discover methods to help learners build a strong foundation in
              reading by understanding the relationship between sounds and
              letters.
            </p>
          </div>
        </Container>
      </Section>
      <Section className="classroom-resources-subpage-resources classroom-resources-subpage-resources--phonics">
        <Container>
          <h2 className="classroom-resources-subpage-resources-title">
            Supporting Resources
          </h2>
          <Row>
            <Column size={4}>
              <Resource
                tophat={phonicsAlphabeticPrinciple.tophat}
                tophatColor={phonicsAlphabeticPrinciple.tophatColor}
                title={phonicsAlphabeticPrinciple.title}
                duration={phonicsAlphabeticPrinciple.duration}
                className={phonicsAlphabeticPrinciple.className}
                subtitle={phonicsAlphabeticPrinciple.subtitle}
                description={phonicsAlphabeticPrinciple.description}
                img={phonicsAlphabeticPrinciple.img}
                actions={phonicsAlphabeticPrinciple.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsDecoding.tophat}
                tophatColor={phonicsDecoding.tophatColor}
                title={phonicsDecoding.title}
                duration={phonicsDecoding.duration}
                className={phonicsDecoding.className}
                subtitle={phonicsDecoding.subtitle}
                description={phonicsDecoding.description}
                img={phonicsDecoding.img}
                actions={phonicsDecoding.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsWordStudy.tophat}
                tophatColor={phonicsWordStudy.tophatColor}
                title={phonicsWordStudy.title}
                duration={phonicsWordStudy.duration}
                className={phonicsWordStudy.className}
                subtitle={phonicsWordStudy.subtitle}
                description={phonicsWordStudy.description}
                img={phonicsWordStudy.img}
                actions={phonicsWordStudy.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card"
              size={12}
            >
              <Resource
                horizontal
                tophat={phonicsMasterclass.tophat}
                tophatColor={phonicsMasterclass.tophatColor}
                title={phonicsMasterclass.title}
                duration={phonicsMasterclass.duration}
                className={phonicsMasterclass.className}
                subtitle={phonicsMasterclass.subtitle}
                description={phonicsMasterclass.description}
                img={phonicsMasterclass.img}
                actions={phonicsMasterclass.actions}
              />
            </Column>
          </Row>
          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card task-cards--phonics"
              size={12}
            >
              <Resource
                horizontal
                tophat={taskCards.tophat}
                tophatColor={taskCards.tophatColor}
                title={taskCards.title}
                duration={taskCards.duration}
                className={taskCards.className}
                subtitle={taskCards.subtitle}
                description={taskCards.description}
                img={taskCards.img}
                actions={taskCards.actions}
              />
            </Column>
          </Row>

          <Row>
            <Column
              className="classroom-resources-subpage-resources-card--horizontal-card task-cards--phonics"
              size={12}
            >
              <Resource
                horizontal
                tophat={phonicsDecodableReaders.tophat}
                tophatColor={phonicsDecodableReaders.tophatColor}
                title={phonicsDecodableReaders.title}
                duration={phonicsDecodableReaders.duration}
                className={phonicsDecodableReaders.className}
                subtitle={phonicsDecodableReaders.subtitle}
                description={phonicsDecodableReaders.description}
                img={phonicsDecodableReaders.img}
                actions={phonicsDecodableReaders.actions}
              />
            </Column>
          </Row>

          <Row>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuDecodableReadersSet1.tophat}
                tophatColor={phonicsRtuDecodableReadersSet1.tophatColor}
                title={phonicsRtuDecodableReadersSet1.title}
                duration={phonicsRtuDecodableReadersSet1.duration}
                className={phonicsRtuDecodableReadersSet1.className}
                subtitle={phonicsRtuDecodableReadersSet1.subtitle}
                description={phonicsRtuDecodableReadersSet1.description}
                img={phonicsRtuDecodableReadersSet1.img}
                actions={phonicsRtuDecodableReadersSet1.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuDecodableReadersSet2.tophat}
                tophatColor={phonicsRtuDecodableReadersSet2.tophatColor}
                title={phonicsRtuDecodableReadersSet2.title}
                duration={phonicsRtuDecodableReadersSet2.duration}
                className={phonicsRtuDecodableReadersSet2.className}
                subtitle={phonicsRtuDecodableReadersSet2.subtitle}
                description={phonicsRtuDecodableReadersSet2.description}
                img={phonicsRtuDecodableReadersSet2.img}
                actions={phonicsRtuDecodableReadersSet2.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuDecodableReadersSet3.tophat}
                tophatColor={phonicsRtuDecodableReadersSet3.tophatColor}
                title={phonicsRtuDecodableReadersSet3.title}
                duration={phonicsRtuDecodableReadersSet3.duration}
                className={phonicsRtuDecodableReadersSet3.className}
                subtitle={phonicsRtuDecodableReadersSet3.subtitle}
                description={phonicsRtuDecodableReadersSet3.description}
                img={phonicsRtuDecodableReadersSet3.img}
                actions={phonicsRtuDecodableReadersSet3.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuAlphabetic.tophat}
                tophatColor={phonicsRtuAlphabetic.tophatColor}
                title={phonicsRtuAlphabetic.title}
                duration={phonicsRtuAlphabetic.duration}
                className={phonicsRtuAlphabetic.className}
                subtitle={phonicsRtuAlphabetic.subtitle}
                description={phonicsRtuAlphabetic.description}
                img={phonicsRtuAlphabetic.img}
                actions={phonicsRtuAlphabetic.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuDecoding.tophat}
                tophatColor={phonicsRtuDecoding.tophatColor}
                title={phonicsRtuDecoding.title}
                duration={phonicsRtuDecoding.duration}
                className={phonicsRtuDecoding.className}
                subtitle={phonicsRtuDecoding.subtitle}
                description={phonicsRtuDecoding.description}
                img={phonicsRtuDecoding.img}
                actions={phonicsRtuDecoding.actions}
              />
            </Column>
            <Column size={4}>
              <Resource
                tophat={phonicsRtuWordStudy.tophat}
                tophatColor={phonicsRtuWordStudy.tophatColor}
                title={phonicsRtuWordStudy.title}
                duration={phonicsRtuWordStudy.duration}
                className={phonicsRtuWordStudy.className}
                subtitle={phonicsRtuWordStudy.subtitle}
                description={phonicsRtuWordStudy.description}
                img={phonicsRtuWordStudy.img}
                actions={phonicsRtuWordStudy.actions}
              />
            </Column>
          </Row>
          <Link
            to="/classroom-resources"
            className="classroom-resources-subpage-header-back-button"
          >
            <Image
              className="classroom-resources-subpage-header-back-icon"
              filename="back-button-icon.png"
              alt="Back"
            />
            <span>Back to Learning Resources Overview</span>
          </Link>
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query PhonicsResources {
    allResourcesJson(filter: { pages: { in: "classroom-resources" } }) {
      edges {
        node {
          slug
          tophat
          tophatColor
          img
          icon
          subject
          title
          subtitle
          duration
          description
          actions {
            label
            metadata
            module
            moduleName
            link
            download
            noIcon
            asLink
            videoGuid
          }
          horizontal
          className
          pages
        }
      }
    }
  }
`;

export default ClassroomResourcesPage;
